import React from "react";
import ReactDOM from "react-dom";

import {observer} from "mobx-react";
import {Popconfirm, PopconfirmProps} from "antd";
import {QuestionCircleOutlined} from "@ant-design/icons";
import {useDefaultZIndex} from "framework/react-integration";
import {CssVariables} from "styles/cssVariables";

export interface AntPopconfirmProps extends PopconfirmProps {
	type?: 'question' | 'message'
}

export const AntPopconfirm = observer((props: AntPopconfirmProps) => {
	const {overlayStyle, ...rest} = props

	const getIcon = React.useCallback(() => {
		switch (props.type) {
			case 'question':
				return <QuestionCircleOutlined style={{color: 'red'}}/>
			default:
				return undefined;
		}
	}, [props.type]);

	const overlayStyleWrapped = useDefaultZIndex(overlayStyle, CssVariables.zIndexControlPopover)

	return <Popconfirm {...props}
	                   overlayStyle={overlayStyleWrapped}
	                   icon={getIcon()}/>;
});

export function showAtElement(left: number, top: number, props: AntPopconfirmProps) : Promise<boolean>{
	return new Promise<boolean>(resolve => {
		const {onVisibleChange, visible, onCancel, onConfirm, ...rest} = props

		const container = document.createElement("div")

		document.body.append(container)
		container.style.position = 'absolute'
		container.style.left = (left - 50) + "px"
		container.style.top = top + "px"
		container.style.width = "100px"

		const onOpenChangedWrapper = (visible: boolean) => {
			onVisibleChange?.(visible)
			ReactDOM.unmountComponentAtNode(container)
		}

		const onConfirmWrapper = (e?: React.MouseEvent<HTMLElement>) => {
			onConfirm?.(e)
			resolve(true)
		}

		const onCancelWrapper = (e?: React.MouseEvent<HTMLElement>) => {
			onCancel?.(e)
			resolve(false)
		}

		ReactDOM.render(
			<AntPopconfirm visible={true}
			               onVisibleChange={onOpenChangedWrapper}
			               onConfirm={onConfirmWrapper}
			               onCancel={onCancelWrapper}
			               {...rest}>
				<div></div>
			</AntPopconfirm>,
			container
		)
	})
}

export type OpenPopConfirmArgs = AntPopconfirmProps & {
	top: number
	left: number
}

export function openPopConfirm(args: OpenPopConfirmArgs) : Promise<boolean>{
	const {top, left, ...rest} = args
	return new Promise<boolean>(resolve => {
		const {onVisibleChange, visible, onCancel, onConfirm, ...rest} = args

		const container = document.createElement("div")

		document.body.append(container)
		container.style.position = 'absolute'
		container.style.left = (left - 50) + "px"
		container.style.top = top + "px"
		container.style.width = "100px"

		const onOpenChangedWrapper = (visible: boolean) => {
			onVisibleChange?.(visible)
			ReactDOM.unmountComponentAtNode(container)
		}

		const onConfirmWrapper = (e?: React.MouseEvent<HTMLElement>) => {
			onConfirm?.(e)
			resolve(true)
		}

		const onCancelWrapper = (e?: React.MouseEvent<HTMLElement>) => {
			onCancel?.(e)
			resolve(false)
		}

		ReactDOM.render(
			<AntPopconfirm visible={true}
						   onVisibleChange={onOpenChangedWrapper}
						   onConfirm={onConfirmWrapper}
						   onCancel={onCancelWrapper}
						   {...rest}>
				<div></div>
			</AntPopconfirm>,
			container
		)
	})
}
